/**
 * Contexte pour les fiches joueur / équipe.
 *
 * Permet d'ouvrir une fiche depuis n'importe quel composant enfant
 * via openPlayerProfile(pseudo) ou openTeamProfile(teamId).
 */

import { createContext, useContext, useState, useCallback } from 'react';

export interface ProfileTarget {
  type: 'player' | 'team';
  id: string; // pseudo for player, team id for team
}

interface ProfileContextValue {
  target: ProfileTarget | null;
  openPlayerProfile: (pseudo: string) => void;
  openTeamProfile: (teamId: string | number) => void;
  close: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useProfile() must be used inside <ProfileProvider>');
  }
  return ctx;
}

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = useState<ProfileTarget | null>(null);

  const openPlayerProfile = useCallback((pseudo: string) => {
    setTarget({ type: 'player', id: pseudo });
  }, []);

  const openTeamProfile = useCallback((teamId: string | number) => {
    setTarget({ type: 'team', id: String(teamId) });
  }, []);

  const close = useCallback(() => {
    setTarget(null);
  }, []);

  return (
    <ProfileContext.Provider value={{ target, openPlayerProfile, openTeamProfile, close }}>
      {children}
    </ProfileContext.Provider>
  );
}
